import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { SwanLogo } from "@/components/Brand";
import api from "@/lib/api";
import {
  BarChart3, CalendarClock, Users, Settings, Shield, LogOut,
  Home, FileText, MessageSquare, User, ChevronDown,
} from "lucide-react";

const NAV = {
  admin: [
    { to: "/", label: "Overview", icon: BarChart3, end: true },
    { to: "/deadlines", label: "Deadlines", icon: CalendarClock },
    { to: "/clients", label: "Clients", icon: Users },
    { to: "/practice", label: "Practice", icon: Settings },
  ],
  staff: [
    { to: "/", label: "Overview", icon: BarChart3, end: true },
    { to: "/deadlines", label: "Deadlines", icon: CalendarClock },
    { to: "/clients", label: "Clients", icon: Users },
    { to: "/security", label: "Security", icon: Shield },
  ],
  client: [
    { to: "/", label: "Home", icon: Home, end: true },
    { to: "/documents", label: "Documents", icon: FileText },
    { to: "/messages", label: "Messages", icon: MessageSquare },
    { to: "/account", label: "Account", icon: User },
  ],
};

const ROLE_LABEL = { admin: "Practice Owner", staff: "Practice Team", client: "Client" };

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = NAV[user.role] || [];
  const [menuOpen, setMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/messages/unread-count").then(({ data }) => setUnread(data.count)).catch(() => {});
  }, []);

  return (
    <div className="swan-bg min-h-screen text-white">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 fixed left-0 top-0 bottom-0 z-40 bg-[#0F172A] border-r border-[#1E294B] p-4">
        <div className="flex items-center gap-3 px-2 py-3 mb-4">
          <SwanLogo size={40} />
          <div>
            <div className="font-serif font-bold text-white leading-tight">JTS Swanson</div>
            <div className="text-[#C9A84A] text-[11px] uppercase tracking-wider">{ROLE_LABEL[user.role]}</div>
          </div>
        </div>
        <nav className="flex flex-col gap-1 flex-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                  isActive ? "bg-[#1E293B] text-white border border-[#C9A84A]/30" : "text-slate-400 hover:text-white hover:bg-white/5"
                }`
              }>
              <n.icon size={19} />
              <span className="font-medium">{n.label}</span>
              {n.label === "Messages" && unread > 0 && (
                <span className="ml-auto bg-[#C9A84A] text-[#0A0F1F] text-xs font-bold rounded-full px-1.5 min-w-[20px] text-center">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <button onClick={logout} data-testid="sidebar-logout"
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 transition-colors">
          <LogOut size={19} /> <span className="font-medium">Sign out</span>
        </button>
      </aside>

      {/* Main */}
      <div className="md:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-[#0A0F1F]/90 backdrop-blur-md border-b border-[#1E294B] px-4 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 md:hidden">
            <SwanLogo size={34} />
            <span className="font-serif font-bold text-white">JTS Swanson</span>
          </div>
          <div className="hidden md:block" />
          <div className="relative">
            <button onClick={() => setMenuOpen((o) => !o)} data-testid="header-menu"
              className="flex items-center gap-2 text-[#C9A84A] uppercase tracking-wider text-xs md:text-sm font-semibold">
              {ROLE_LABEL[user.role]} <ChevronDown size={16} />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 mt-2 w-52 card-surface rounded-xl p-2 z-50 shadow-2xl">
                  <div className="px-3 py-2 border-b border-[#1E294B] mb-1">
                    <div className="text-white font-medium text-sm truncate">{user.name}</div>
                    <div className="text-slate-500 text-xs truncate">{user.email}</div>
                  </div>
                  <button onClick={() => { setMenuOpen(false); navigate(user.role === "client" ? "/account" : "/security"); }}
                    className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-slate-300 hover:bg-white/5" data-testid="menu-security">
                    <Shield size={16} /> Security
                  </button>
                  <button onClick={logout} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-slate-300 hover:bg-white/5" data-testid="menu-logout">
                    <LogOut size={16} /> Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        <main className="px-4 md:px-8 py-6 pb-28 md:pb-10 max-w-6xl mx-auto animate-fade-up">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-[#0F172A]/95 backdrop-blur-xl border-t border-[#1E294B] px-2 py-2 flex justify-around items-center md:hidden" style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}>
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`tab-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg relative ${isActive ? "text-[#C9A84A]" : "text-slate-400"}`
            }>
            <n.icon size={22} />
            <span className="text-[11px] font-medium">{n.label}</span>
            {n.label === "Messages" && unread > 0 && (
              <span className="absolute top-0 right-1 bg-[#C9A84A] text-[#0A0F1F] text-[10px] font-bold rounded-full px-1 min-w-[16px] text-center">{unread}</span>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
